import React, { useState, useEffect } from 'react';

const CalendarPicker = ({ selectedDate, onDateSelect, label }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));

  // Sync month with selected date
  useEffect(() => {
    setCurrentMonth(new Date(selectedDate));
  }, [selectedDate]);

  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  const weekDays = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];

  // Add styles
  const styles = `
    .calendar-label {
      display: block;
      color: rgba(255, 200, 140, 0.8);
      font-size: 12px;
      margin-bottom: 8px;
      font-weight: 500;
      font-family: var(--font-display);
    }

    .calendar {
      background: transparent;
    }

    .calendar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding: 0 4px;
    }

    .calendar-nav-button {
      background: rgba(255, 120, 60, 0.2);
      border: 1px solid rgba(255, 120, 60, 0.3);
      border-radius: 4px;
      color: rgba(255, 220, 160, 0.9);
      cursor: pointer;
      font-size: 16px;
      font-weight: bold;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      font-family: var(--font-display);
    }

    .calendar-nav-button:hover {
      background: rgba(255, 120, 60, 0.4);
      border-color: rgba(255, 120, 60, 0.5);
    }

    .calendar-month {
      color: rgba(255, 220, 160, 0.9);
      font-size: 14px;
      font-weight: 600;
      font-family: var(--font-display);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }

    .calendar-weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
      margin-bottom: 4px;
    }

    .calendar-weekday {
      text-align: center;
      color: rgba(255, 180, 100, 0.7);
      font-size: 11px;
      font-weight: 600;
      padding: 4px;
      font-family: var(--font-display);
    }

    .calendar-days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
    }

    .calendar-day {
      aspect-ratio: 1;
      border: none;
      background: transparent;
      color: rgba(255, 200, 140, 0.8);
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
      border-radius: 4px;
      transition: all 0.2s ease;
      font-family: var(--font-display);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .calendar-day:hover {
      background: rgba(255, 120, 60, 0.3);
      color: white;
    }

    .calendar-day.selected {
      background: linear-gradient(135deg, rgba(255, 120, 60, 0.8), rgba(255, 80, 30, 0.7));
      border: 1px solid rgba(255, 120, 60, 0.6);
      color: white;
      font-weight: 600;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    .calendar-day.today {
      border: 1px solid rgba(255, 200, 100, 0.6);
      color: rgba(255, 220, 100, 0.9);
    }

    .calendar-day.today.selected {
      border-color: rgba(255, 120, 60, 0.8);
    }

    .calendar-day.empty {
      cursor: default;
      visibility: hidden;
    }
  `;

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert to Monday-based week (0 = Monday, 6 = Sunday)
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  // Get days in month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateClick = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onDateSelect(newDate.toISOString().split('T')[0]);
  };

  const renderDays = () => {
    const days = [];
    const firstDay = getFirstDayOfMonth(currentMonth);
    const daysInMonth = getDaysInMonth(currentMonth);
    const today = new Date();
    const selected = new Date(selectedDate);

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = today.getDate() === day &&
                     today.getMonth() === currentMonth.getMonth() &&
                     today.getFullYear() === currentMonth.getFullYear();
      const isSelected = selected.getDate() === day &&
                        selected.getMonth() === currentMonth.getMonth() &&
                        selected.getFullYear() === currentMonth.getFullYear();

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <>
      <style>{styles}</style>
      <div className="calendar-picker">
        {label && <label className="calendar-label">{label}</label>}

        <div className="calendar">
          {/* Header */}
          <div className="calendar-header">
            <button
              type="button"
              onClick={handlePreviousMonth}
              className="calendar-nav-button"
            >
              ‹
            </button>
            <div className="calendar-month">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="calendar-nav-button"
            >
              ›
            </button>
          </div>

          {/* Week days */}
          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="calendar-days">
            {renderDays()}
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarPicker;